import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, Briefcase, CheckSquare, Square } from 'lucide-react';

export default function Register() {
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    business_name: ''
  });
  const [loading, setLoading] = useState(false);
  const [soloPrice, setSoloPrice] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const hasTracked = useRef(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Track page visit for analytics (with guard against StrictMode double-fire)
  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    
    const trackVisit = async () => {
      try {
        await fetch(`${API_URL}/api/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: 'register',
            referrer: document.referrer || null,
            user_agent: navigator.userAgent
          })
        });
      } catch (err) {
        // Silently fail - analytics shouldn't break the app
        console.debug('Analytics tracking failed:', err);
      }
    };
    trackVisit();
  }, [API_URL]);
  
  // Fetch Solo plan price dynamically
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(`${API_URL}/api/plans`);
        if (response.ok) {
          const data = await response.json();
          // Handle both array and {plans: [...]} response formats
          const plans = Array.isArray(data) ? data : (data.plans || []);
          const soloPlan = plans.find(p => p.plan_id === 'solo_pro' || p.id === 'solo');
          if (soloPlan) {
            setSoloPrice(soloPlan.price);
          }
        }
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      }
    };
    fetchPlans();
  }, [API_URL]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(formData.email, formData.password, formData.name, formData.business_name, acceptedTerms);
      toast.success('Account created!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-darker flex flex-col">
      {/* Header */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <img 
          src="/favicon.png" 
          alt="MyJobTools" 
          className="w-16 h-16 mb-2"
        />
        <h1 className="font-heading text-2xl font-bold italic mb-1">
          <span className="text-slate-200">MyJob</span>
          <span className="text-[#FF5F00]">Tools</span>
        </h1>
        <p className="text-slate-400 text-center mb-6">
          Get your toolbox set up in 60 seconds.
        </p>
        
        {/* Register Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-slate-300 uppercase text-xs tracking-wider font-bold">
              Your Name
            </Label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="name"
                name="name"
                data-testid="register-name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Smith"
                className="h-14 pl-12 bg-slate-dark border-2 border-slate-light text-white placeholder:text-slate-500 text-lg rounded-lg"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="business_name" className="text-slate-300 uppercase text-xs tracking-wider font-bold">
              Business Name <span className="text-slate-500">(optional)</span>
            </Label>
            <div className="relative">
              <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="business_name"
                name="business_name"
                data-testid="register-business"
                type="text"
                value={formData.business_name}
                onChange={handleChange}
                placeholder="Smith Plumbing"
                className="h-14 pl-12 bg-slate-dark border-2 border-slate-light text-white placeholder:text-slate-500 text-lg rounded-lg"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-300 uppercase text-xs tracking-wider font-bold">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="email"
                name="email"
                data-testid="register-email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className="h-14 pl-12 bg-slate-dark border-2 border-slate-light text-white placeholder:text-slate-500 text-lg rounded-lg"
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-300 uppercase text-xs tracking-wider font-bold">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="password"
                name="password"
                data-testid="register-password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                className="h-14 pl-12 bg-slate-dark border-2 border-slate-light text-white placeholder:text-slate-500 text-lg rounded-lg"
                required
                minLength={6}
              />
            </div>
          </div>

          {/* Terms of Service Checkbox */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setAcceptedTerms(!acceptedTerms)}
              className="flex items-start gap-3 w-full text-left group"
              data-testid="terms-checkbox"
            >
              <div className="mt-0.5 flex-shrink-0">
                {acceptedTerms ? (
                  <CheckSquare className="w-5 h-5 text-[#FF5F00]" />
                ) : (
                  <Square className="w-5 h-5 text-slate-400 group-hover:text-slate-300" />
                )}
              </div>
              <span className="text-sm text-slate-400 leading-relaxed">
                I agree to the{' '}
                <Link 
                  to="/terms" 
                  target="_blank"
                  className="text-[#FF5F00] hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link 
                  to="/privacy" 
                  target="_blank"
                  className="text-[#FF5F00] hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </Link>
              </span>
            </button>
          </div>
          
          <Button
            type="submit"
            data-testid="register-submit"
            disabled={loading || !acceptedTerms}
            className="w-full h-14 bg-[#FF5F00] hover:bg-orange-hover text-white font-bold uppercase tracking-wide text-lg rounded-lg shadow-hard active:translate-y-0.5 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
        
        <p className="mt-6 text-slate-400">
          Already have an account?{' '}
          <Link to="/login" className="text-[#FF5F00] font-bold hover:underline" data-testid="login-link">
            Sign In
          </Link>
        </p>
      </div>
      
      {/* Pricing note */}
      <div className="p-6 text-center">
        <p className="text-slate-500 text-sm">
          {soloPrice ? `Start free, then $${soloPrice}/mo` : 'Start free today'} • No contracts • Cancel anytime
        </p>
      </div>
    </div>
  );
}
